/* NOT PAIRED: a revoked or missing token. Staff type the six-character code shown in the admin, or scan its QR
   code, which opens /pair?code=XXXXXX and pairs straight away. */
import { createSignal, onMount } from 'solid-js';
import { Faceplate, Glass, Key, Readout } from '../../hifi';
import { api, ApiError, DeviceCreds } from '../../api';

type PairResult = { device_id: string; device_key: string; name: string; zone: { id: string; name: string }; endpoint: { token: string } };

const clean = (v: string) => v.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);

export default function Pairing(props: { onPaired: (d: DeviceCreds, token: string) => void }) {
  const [code, setCode] = createSignal('');
  const [msg, setMsg] = createSignal('ENTER THE CODE FROM THE ADMIN');
  const [busy, setBusy] = createSignal(false);

  onMount(() => {
    const fromLink = clean(new URLSearchParams(location.search).get('code') ?? '');
    history.replaceState(null, '', '/pair');  // the code is single-use: keep it out of history and reloads
    if (fromLink.length === 6) { setCode(fromLink); setMsg('PAIRING…'); pair(); }
  });

  async function pair(e?: Event) {
    e?.preventDefault();
    if (code().length !== 6 || busy()) return;
    setBusy(true);
    try {
      const r = await api<PairResult>('/device/pair', { method: 'POST',
        body: JSON.stringify({ code: code(), kind: 'web' }) });  // keep the name given in the admin
      props.onPaired({ device_id: r.device_id, device_key: r.device_key, name: r.name, zone_id: r.zone.id }, r.endpoint.token);
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      setMsg(status === 404 ? 'UNKNOWN OR EXPIRED CODE' : status === 429 ? 'TOO MANY WRONG CODES, WAIT 15 MIN' : 'NETWORK ERROR, TRY AGAIN');
    } finally { setBusy(false); }
  }

  return (
    <div class="mx-auto flex h-screen max-h-[768px] w-full max-w-[1024px] flex-col gap-[14px] p-5">
      <Faceplate as="header" class="flex items-center gap-2.5 px-5 py-3">
        <img src="/mark.png" alt="" class="h-7 w-10 object-contain" />
        <span class="wordmark text-xl font-semibold">scai.fm</span>
        <span class="wordmark text-xl" style={{ color: 'var(--vfd)' }}>Zones</span>
      </Faceplate>
      <Faceplate as="main" class="flex flex-grow flex-col items-center justify-center gap-6 p-10">
        <Glass class="flex w-full max-w-[640px] flex-col gap-4 px-8 py-7">
          <Readout size="xl" text="NOT PAIRED" />
          <Readout size="m" tone="muted" text={msg()} live />
          <div class="text-[48px] leading-[52px] tracking-[0.3em] vfd-xl">{code().padEnd(6, '_')}</div>
        </Glass>
        <form class="flex items-end gap-3" onSubmit={pair}>
          <label class="flex flex-col gap-2">
            <span class="legend">Pairing code</span>
            <input value={code()} maxLength={6} autocomplete="off" autocapitalize="characters" spellcheck={false}
              onInput={(e) => setCode(clean(e.currentTarget.value))}
              class="h-[52px] w-56 rounded-[3px] border border-edge bg-glass px-4 font-mono text-2xl tracking-[0.3em] text-legend" />
          </label>
          <Key size="kiosk" primary legend="Pair" type="submit" disabled={code().length !== 6 || busy()} />
        </form>
      </Faceplate>
    </div>
  );
}
